import React, { useState, useEffect } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    Select,
    Input,
    IconButton,
    Badge,
    Card,
    CardBody,
    FormControl,
    FormLabel,
    Divider,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useToast } from '../hooks/useToast';
import fetchWithAuth from '../utils/fetchWithAuth';

const OPERATORS = [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '!=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '>=' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '<=' },
    { value: 'contains', label: 'contains' },
];

function SubframeFilterBuilder({ populationId, value = [], onChange, onSubframeSizeChange }) {
    const [filters, setFilters] = useState(value || []);
    const [columns, setColumns] = useState([]);
    const [columnMetadata, setColumnMetadata] = useState({});
    const [subframeSize, setSubframeSize] = useState(null);
    const [populationSize, setPopulationSize] = useState(null);
    const toast = useToast();

    useEffect(() => {
        if (populationId) {
            fetchPopulationData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [populationId]);

    useEffect(() => {
        if (onChange) {
            onChange(filters);
        }

        // Only calculate if we have complete filters (all have values)
        const completeFilters = filters.filter(f => f.column && f.operator && f.value !== '' && f.value !== null && f.value !== undefined);

        console.log('SubframeFilterBuilder effect triggered:', {
            filters,
            completeFilters,
            populationId,
            populationSize
        });

        if (populationId && completeFilters.length > 0) {
            console.log('Calling calculateSubframeSize with filters:', completeFilters);
            calculateSubframeSize();
        } else if (filters.length === 0 || completeFilters.length === 0) {
            console.log('No complete filters, using population size:', populationSize);
            setSubframeSize(populationSize);
            if (onSubframeSizeChange) {
                onSubframeSizeChange(populationSize);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, populationId, populationSize]);

    const fetchPopulationData = async () => {
        try {
            const response = await fetchWithAuth(`/api/populations/${populationId}/demographics/`);
            const data = await response.json();
            setColumns(data.columns || []);
            setPopulationSize(data.total_individuals);
            setSubframeSize(data.total_individuals);

            // Build metadata about each column (type and unique values)
            const metadata = {};
            for (const [column, breakdown] of Object.entries(data.demographic_breakdowns || {})) {
                const values = breakdown.values.map(v => v.value);

                // Detect if column is numeric (all values are numbers)
                const isNumeric = values.every(v => !isNaN(Number(v)));

                metadata[column] = {
                    isNumeric,
                    uniqueValues: values,
                    total_unique: breakdown.total_unique
                };
            }
            setColumnMetadata(metadata);
        } catch (error) {
            toast.showError('Error', 'Failed to fetch population columns');
        }
    };

    const calculateSubframeSize = async () => {
        try {
            console.log('calculateSubframeSize called with filters:', filters);

            // Import CSRF token getter
            const getCSRFtoken = (await import('../utils/getCSRFtoken')).default;
            const csrfToken = getCSRFtoken();

            const response = await fetchWithAuth(`/api/populations/${populationId}/subframe-size/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ filters }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                toast.showError('Error', 'Failed to calculate subframe size. Check console for details.');
                return;
            }

            const data = await response.json();
            console.log('Subframe size response:', data);
            setSubframeSize(data.subframe_size);
            if (onSubframeSizeChange) {
                onSubframeSizeChange(data.subframe_size);
            }
        } catch (error) {
            console.error('Failed to calculate subframe size:', error);
        }
    };

    const addFilter = () => {
        const newFilter = {
            column: columns[0] || '',
            operator: 'eq',
            value: '',
        };
        setFilters([...filters, newFilter]);
    };

    const updateFilter = (index, field, value) => {
        const updated = [...filters];
        updated[index][field] = value;

        // Auto-convert value to number if it looks numeric
        if (field === 'value' && value !== '') {
            const numValue = Number(value);
            if (!isNaN(numValue) && value.trim() !== '') {
                updated[index][field] = numValue;
            }
        }

        setFilters(updated);
    };

    const removeFilter = (index) => {
        setFilters(filters.filter((_, i) => i !== index));
    };

    const getAvailableOperators = (column) => {
        const metadata = columnMetadata[column];

        if (!metadata) {
            return OPERATORS; // Show all if no metadata
        }

        const { isNumeric } = metadata;

        if (isNumeric) {
            // Numeric columns: show all comparison operators
            return OPERATORS;
        } else {
            // Categorical/text columns: only eq, neq, contains
            return OPERATORS.filter(op => ['eq', 'neq', 'contains'].includes(op.value));
        }
    };

    const renderValueInput = (filter, index) => {
        const column = filter.column;
        const metadata = columnMetadata[column];

        if (!metadata) {
            // Fallback to text input if no metadata
            return (
                <Input
                    value={filter.value}
                    onChange={(e) => updateFilter(index, 'value', e.target.value)}
                    placeholder="Enter value"
                    size="sm"
                />
            );
        }

        const { isNumeric, uniqueValues, total_unique } = metadata;

        // For numeric columns with comparison operators, show number input
        if (isNumeric && ['gt', 'gte', 'lt', 'lte'].includes(filter.operator)) {
            return (
                <Input
                    type="number"
                    value={filter.value}
                    onChange={(e) => updateFilter(index, 'value', e.target.value)}
                    placeholder="Enter number"
                    size="sm"
                />
            );
        }

        // For categorical columns or eq/neq operators, show dropdown
        // But only if we have a reasonable number of unique values (< 50)
        if (total_unique <= 50) {
            return (
                <Select
                    value={filter.value}
                    onChange={(e) => updateFilter(index, 'value', e.target.value)}
                    placeholder="Select value"
                    size="sm"
                >
                    {uniqueValues.map((val, idx) => (
                        <option key={idx} value={val}>{val}</option>
                    ))}
                </Select>
            );
        }

        // For columns with too many unique values, show text input with hint
        return (
            <Input
                value={filter.value}
                onChange={(e) => updateFilter(index, 'value', e.target.value)}
                placeholder={`Enter value (${total_unique} unique values)`}
                size="sm"
            />
        );
    };

    if (!populationId) {
        return (
            <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                <Text color="gray.500">Select a population first to configure subframe filters</Text>
            </Box>
        );
    }

    return (
        <Card>
            <CardBody>
                <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                        <Box>
                            <Text fontWeight="bold" mb={1}>Subframe Filters</Text>
                            <Text fontSize="sm" color="gray.600">
                                Limit audience to specific demographic criteria
                            </Text>
                        </Box>
                        {subframeSize !== null && populationSize !== null && (
                            <Badge colorScheme={filters.length > 0 ? 'purple' : 'gray'} fontSize="md" p={2}>
                                {subframeSize.toLocaleString()} / {populationSize.toLocaleString()} individuals
                                {filters.length > 0 && ` (${Math.round(subframeSize / populationSize * 100)}%)`}
                            </Badge>
                        )}
                    </HStack>

                    <Divider />

                    {filters.length === 0 ? (
                        <Box p={4} bg="blue.50" borderRadius="md" textAlign="center">
                            <Text fontSize="sm" color="blue.800" mb={2}>
                                No filters applied - audience will include all {populationSize?.toLocaleString() || 0} individuals from this population
                            </Text>
                            <Button size="sm" leftIcon={<AddIcon />} onClick={addFilter} colorScheme="blue">
                                Add Filter
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <VStack align="stretch" spacing={3}>
                                {filters.map((filter, index) => (
                                    <Box key={index} p={3} bg="gray.50" borderRadius="md">
                                        <HStack spacing={2}>
                                            <FormControl flex={2}>
                                                <FormLabel fontSize="xs" mb={1}>Column</FormLabel>
                                                <Select
                                                    value={filter.column}
                                                    onChange={(e) => updateFilter(index, 'column', e.target.value)}
                                                    size="sm"
                                                >
                                                    {columns.map(col => (
                                                        <option key={col} value={col}>{col}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl flex={1}>
                                                <FormLabel fontSize="xs" mb={1}>Operator</FormLabel>
                                                <Select
                                                    value={filter.operator}
                                                    onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                                                    size="sm"
                                                >
                                                    {getAvailableOperators(filter.column).map(op => (
                                                        <option key={op.value} value={op.value}>{op.label}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl flex={2}>
                                                <FormLabel fontSize="xs" mb={1}>Value</FormLabel>
                                                {renderValueInput(filter, index)}
                                            </FormControl>

                                            <IconButton
                                                icon={<DeleteIcon />}
                                                onClick={() => removeFilter(index)}
                                                colorScheme="red"
                                                variant="ghost"
                                                size="sm"
                                                aria-label="Remove filter"
                                                mt={5}
                                            />
                                        </HStack>
                                    </Box>
                                ))}
                            </VStack>

                            <Button size="sm" leftIcon={<AddIcon />} onClick={addFilter} variant="outline">
                                Add Another Filter
                            </Button>
                        </>
                    )}

                    {filters.length > 0 && (
                        <Box p={3} bg="yellow.50" borderRadius="md">
                            <Text fontSize="xs" color="yellow.800">
                                <strong>Note:</strong> All filters are applied with AND logic. An individual must match ALL criteria to be included in the audience.
                            </Text>
                        </Box>
                    )}
                </VStack>
            </CardBody>
        </Card>
    );
}

export default SubframeFilterBuilder;
